import LoadingItem from "react-loading";
import { Alert, Progress, Table, Steps } from "antd";
import React, { useEffect, useState } from "react";
import { Checkmark } from "react-checkmark";
import LoadingIcon from "@ant-design/icons/LoadingOutlined";
import ClockIcon from "@ant-design/icons/ClockCircleOutlined";
import { AppManager } from "../../../../manager";
import { ICompanyInfo, ICustomerInfo } from "../../../../models/Apps/Migration/XPlanTypes";
import AnimatedStatusHeader from "./AnimatedStatusHeader";
import Styles from "./Home.scss";

const ExpandedDetails: React.FC<{ record: ICompanyInfo }> = ({ record }) => {
    const currentStep = !record.keyDetailsPageScraped
        ? 0
        : !record.contactDetailsPageScraped
        ? 1
        : !record.fileNotesDataScraped
        ? 2
        : !record.isAllNotesAttachmentsUploaded
        ? 3
        : 4;
    return (
        <div className={Styles.MigrationStepView}>
            <div className={Styles.Steps}>
                <Steps current={currentStep} size="small">
                    <Steps.Step
                        title={currentStep == 0 ? "Loading Key Details" : "Key Details"}
                        description={currentStep == 0 ? "In-Progress" : currentStep > 0 ? "Done" : ""}
                        icon={currentStep == 0 ? <LoadingIcon /> : undefined}
                    />
                    <Steps.Step
                        title={currentStep == 1 ? "Loading Contact Details" : "Contact Details"}
                        description={currentStep == 1 ? "In-Progress" : currentStep > 1 ? "Done" : ""}
                        icon={currentStep == 1 ? <LoadingIcon /> : undefined}
                    />
                    <Steps.Step
                        title={currentStep == 2 ? "Loading File Notes" : "File Notes"}
                        description={currentStep == 2 ? "In-Progress" : currentStep > 2 ? "Done" : ""}
                        icon={currentStep == 2 ? <LoadingIcon /> : undefined}
                    />
                    <Steps.Step
                        title={currentStep == 3 ? "Loading Attachments" : "Attachments"}
                        description={currentStep == 3 ? "In-Progress" : currentStep > 3 ? "Done" : ""}
                        icon={currentStep == 3 ? <LoadingIcon /> : undefined}
                    />
                </Steps>
            </div>
        </div>
    );
};

const MigrateCompaniesView: React.FC<{ customer: ICustomerInfo }> = ({ customer }) => {
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState<ICompanyInfo[]>([]);
    const [completedCount, setCompletedCount] = useState(0);
    const [totalCount, setTotalCount] = useState(1);
    const [error, setError] = useState("");
    const loadCompanies = () => {
        setLoading(true);
        AppManager.ausplanApps.xplanMigrator.apiFetchCompanies(customer.ausplanUserId, (err, data) => {
            setLoading(false);
            if (err) setError(`${err}`);
            else if (data) {
                setCompanies(data);
            }
        });
    };
    const getCurrentRow = () => {
        for (let i = 0; i < companies.length; i++) {
            if (!companies[i].fullyScrapped && (i == 0 || companies[i - 1].fullyScrapped)) {
                return companies[i];
            }
        }

        return undefined;
    };
    useEffect(() => {
        loadCompanies();
    }, [customer]);
    useEffect(() => {
        setTotalCount(companies.length);
        setCompletedCount(companies.filter((r) => r.fullyScrapped).length);
    }, [companies]);

    const currentRow = getCurrentRow();

    return (
        <div className={Styles.MigrationView}>
            <AnimatedStatusHeader />
            <div className={Styles.MigrationItems}>
                <div className={Styles.StatusHeader}>
                    <div>
                        Companies Migration Status ({completedCount}/{totalCount})
                    </div>
                    <div className={Styles.ProgressContainer}>
                        <Progress strokeWidth={12} type="line" percent={+((completedCount * 100) / totalCount).toFixed(1)} />
                    </div>
                </div>
                {error && <Alert type="error" message={error} closable onClose={() => setError("")} style={{ marginBottom: 15 }} />}
                <Table
                    dataSource={companies}
                    rowKey={(rec) => rec.pageId}
                    columns={[
                        { title: "#Id", render: (_, rec) => <b>{rec.pageId}</b> },
                        { title: "Company Name", render: (_, rec) => rec.keyDetails?.keyDetailsSection?.companyName || "--" },
                        { title: "Address", render: (_, rec) => rec.contactDetails?.addressObject?.address || "--" },
                        {
                            title: "",
                            width: 50,
                            render: (_, rec) =>
                                rec.fullyScrapped ? (
                                    <Checkmark size={20} />
                                ) : currentRow?.pageId == rec.pageId ? (
                                    <LoadingItem type="cubes" color="#555" height={15} width={30} />
                                ) : (
                                    <ClockIcon style={{ fontSize: 18 }} />
                                ),
                        },
                    ]}
                    loading={!companies.length && loading}
                    size="small"
                    pagination={false}
                    expandable={{
                        expandedRowRender: (record) => <ExpandedDetails record={record} />,
                        expandedRowKeys: [currentRow?.pageId || ""],
                        rowExpandable: (record) => currentRow?.pageId === record.pageId,
                    }}
                />
            </div>
        </div>
    );
};

export default MigrateCompaniesView;
